import { Box, Typography, Radio, Chip, Tooltip, Switch, FormControlLabel, Button } from '@mui/material';
import hexContrastColor from 'hex-contrast-color';
import { getWordWithNumber } from 'src/utils';
import useIsMobileSize from 'src/hooks/useIsMobileSize';
import formatNumber from 'src/utils/formatNumber';
import { AIIcon } from 'src/icons/AIIcon';
import dayjs from 'dayjs';
import ArrowRight from 'src/icons/ArrowRight';
import TransparentButton from 'src/components/TransparentButton';
import getPeriodsInfo from 'src/utils/getPeriodInfo';

// Иконки для опций тарифа
const SpamFilterIcon = () => (
    <Box
        component="span"
        sx={{ display: 'inline-flex', alignItems: 'center', mr: 0.5 }}
    >
        🛡️
    </Box>
);

const TelegramNotificationsIcon = () => (
    <Box
        component="span"
        sx={{ display: 'inline-flex', alignItems: 'center', mr: 0.5 }}
    >
        📱
    </Box>
);

const ExportLeadsIcon = () => (
    <Box
        component="span"
        sx={{ display: 'inline-flex', alignItems: 'center', mr: 0.5 }}
    >
        📊
    </Box>
);

const SetupServiceIcon = () => (
    <Box
        component="span"
        sx={{ display: 'inline-flex', alignItems: 'center', mr: 0.5 }}
    >
        👤
    </Box>
);

type SubscriptionItemProps = {
    checkedValue: unknown;
    value: unknown;
    price: number;
    bundles?: number;
    chats?: number;
    keywords?: number;
    projects?: number;
    color?: string;
    title: string;
    ai_analysis_enabled?: boolean;
    spam_filter_enabled?: boolean;
    telegram_notifications_enabled?: boolean;
    leads_export_enabled?: boolean;
    setup_service_enabled?: boolean;
    description?: string;
    isRecommended?: boolean;
    disabled?: boolean;
    disabledReason?: string;
    onChange?: (value: unknown) => void;
    // Новые пропсы для улучшений
    potential_clients_per_month?: number;
    monthlyPriceWithDiscount?: number; // Цена за месяц с учетом скидки за период
    selectedDurationMonths?: number; // Выбранный период для расчета стоимости лида
    // Для индивидуального тарифа
    isCustomPlan?: boolean;
    customPlanAIAnalysis?: boolean;
    onCustomPlanAIAnalysisChange?: (enabled: boolean) => void;
    aiAnalysisPrice?: number;
    // Конфигуратор индивидуального тарифа
    customPlanConfigurator?: React.ReactNode;
    // Для текущего тарифа
    isCurrentTariff?: boolean;
    start_date?: string;
    end_date?: string;
    is_active?: boolean;
    is_recurring?: boolean;
    duration_months?: number;
    onUpgradeToAnnual?: () => void;
    onCancelSubscription?: () => void;
    onSelectOtherTariff?: () => void;
    isCanceling?: boolean;
};

const TariffNameChip = ({ name }: { name: string }) => {
    return (
        <Typography
            variant="h4"
            sx={{
                color: 'var(--lm-text)',
                fontWeight: 900,
                fontSize: 24,
                lineHeight: 1.2,
            }}
        >
            {name}
        </Typography>
    );
};

const SubscriptionItem = ({
    checkedValue,
    value,
    price,
    title,
    bundles = 0,
    keywords = 0,
    chats = 0,
    projects = 0,
    color,
    ai_analysis_enabled,
    spam_filter_enabled,
    telegram_notifications_enabled,
    leads_export_enabled,
    setup_service_enabled,
    description,
    isRecommended = false,
    disabled = false,
    disabledReason,
    onChange,
    isCustomPlan = false,
    customPlanAIAnalysis = false,
    onCustomPlanAIAnalysisChange,
    aiAnalysisPrice = 0,
    customPlanConfigurator,
    isCurrentTariff = false,
    start_date,
    end_date,
    is_active,
    is_recurring,
    duration_months,
    potential_clients_per_month,
    monthlyPriceWithDiscount,
    selectedDurationMonths,
    onUpgradeToAnnual,
    onCancelSubscription,
    onSelectOtherTariff,
    isCanceling = false,
}: SubscriptionItemProps) => {
    const checked = value === checkedValue;
    const contrastColor = hexContrastColor(color);
    const isMobile = useIsMobileSize();

    const borderColor = isMobile ? 'var(--lm-border)' : '#CCC';
    const tariffColor = color || 'var(--lm-text)';

    const handleClick = () => {
        if (disabled || !onChange) return;
        onChange(value);
    };

    const cardContent = (
        <Box
            display="block"
            component={'label'}
            border={checked ? '2px solid' : '1px solid'}
            borderColor={checked ? 'primary' : borderColor}
            bgcolor={isMobile ? 'transparent' : 'background.default'}
            borderRadius={4}
            style={{
                cursor: disabled ? 'not-allowed' : 'pointer',
                position: 'relative',
                height: '100%',
                opacity: disabled ? 0.6 : 1,
            }}
            sx={{
                display: 'flex',
                flexDirection: 'column',
            }}
            p={2}
            onClick={handleClick}
        >
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                mb={2}
            >
                <Box
                    flex={1}
                    minWidth={0}
                >
                    <TariffNameChip name={title} />
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--lm-text)',
                            fontWeight: 600,
                            fontSize: 18,
                            mt: 0.5,
                        }}
                    >
                        {monthlyPriceWithDiscount ? formatNumber(monthlyPriceWithDiscount) : formatNumber(price)} ₽/мес
                        {monthlyPriceWithDiscount && monthlyPriceWithDiscount < price && (
                            <Typography
                                component="span"
                                sx={{
                                    color: 'var(--lm-muted)',
                                    fontWeight: 400,
                                    fontSize: 14,
                                    textDecoration: 'line-through',
                                    ml: 1,
                                }}
                            >
                                {formatNumber(price)}
                            </Typography>
                        )}
                    </Typography>
                    {/* Стоимость лида */}
                    {potential_clients_per_month &&
                        potential_clients_per_month > 0 &&
                        selectedDurationMonths &&
                        monthlyPriceWithDiscount && (
                            <Typography
                                variant="body2"
                                sx={{
                                    color: '#0044FF',
                                    fontWeight: 500,
                                    fontSize: 14,
                                    mt: 0.5,
                                }}
                            >
                                {(monthlyPriceWithDiscount / potential_clients_per_month).toFixed(2)} ₽/лид
                            </Typography>
                        )}
                </Box>
                <Radio
                    value={value}
                    checked={checked}
                    disabled={disabled}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (!disabled && onChange) {
                            onChange(value);
                        }
                    }}
                />
            </Box>
            {description && (
                <Typography
                    sx={{
                        fontSize: 14,
                        fontWeight: 400,
                        mb: 1,
                        mt: 1,
                        wordBreak: 'break-word',
                        color: 'var(--lm-muted)',
                    }}
                >
                    {description}
                </Typography>
            )}

            {/* Информационные блоки - "Лучшее соотношение" только для тарифа "Про" */}
            {!isCustomPlan && title === 'Про' && (
                <Box
                    sx={{
                        mb: 1.5,
                        p: 1.5,
                        backgroundColor: 'rgba(0, 68, 255, 0.05)',
                        borderRadius: 2,
                        border: '1px solid rgba(0, 68, 255, 0.1)',
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 14,
                            fontWeight: 500,
                            color: '#0044FF',
                            textAlign: 'center',
                        }}
                    >
                        💡 Лучшее соотношение цена/результат
                    </Typography>
                </Box>
            )}

            {/* Блок "Подходит, если вы:" для базовых тарифов */}
            {!isCustomPlan && price < 3000 && (
                <Box
                    sx={{
                        mb: 1.5,
                        p: 1.5,
                        backgroundColor: 'rgba(255, 193, 7, 0.05)',
                        borderRadius: 2,
                        border: '1px solid rgba(255, 193, 7, 0.2)',
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 13,
                            fontWeight: 600,
                            mb: 0.5,
                            color: 'var(--lm-text)',
                        }}
                    >
                        ❗ Подходит, если вы:
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: 13,
                            fontWeight: 400,
                            color: 'var(--lm-muted)',
                            lineHeight: 1.4,
                        }}
                    >
                        — тестируете нишу
                        <br />— готовы фильтровать лиды вручную
                    </Typography>
                </Box>
            )}

            {/* Блок для тарифа "Бизнес" */}
            {!isCustomPlan && price >= 6000 && (
                <Box
                    sx={{
                        mb: 1.5,
                        p: 1.5,
                        backgroundColor: 'rgba(99, 102, 241, 0.08)',
                        borderRadius: 2,
                        border: '1px solid rgba(99, 102, 241, 0.2)',
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 14,
                            fontWeight: 500,
                            color: 'rgb(99, 102, 241)',
                            textAlign: 'center',
                        }}
                    >
                        🏢 Используют агентства и отделы продаж
                    </Typography>
                </Box>
            )}

            {/* Опции и функции (только для не-индивидуальных тарифов) */}
            {!isCustomPlan && (
                <Box
                    mt={1}
                    mb={1}
                >
                    <Typography
                        variant="body2"
                        color="text.primary"
                        sx={{
                            fontWeight: 600,
                            mb: 1,
                            fontSize: '14px',
                        }}
                    >
                        Включено в тариф:
                    </Typography>

                    {/* Базовые параметры тарифа (проекты, чаты, подборки, ключевые слова) */}
                    <Box
                        display="flex"
                        flexDirection="row"
                        flexWrap="wrap"
                        gap={1}
                        mb={1}
                    >
                        {(
                            [
                                projects > 0
                                    ? {
                                          key: 'projects',
                                          label: getWordWithNumber(projects, ['проект', 'проекта', 'проектов']),
                                          tooltip: '',
                                      }
                                    : null,
                                bundles > 0
                                    ? {
                                          key: 'bundles',
                                          label: getWordWithNumber(bundles, ['подборка', 'подборки', 'подборок']),
                                          tooltip: '',
                                      }
                                    : null,
                                chats > 0
                                    ? {
                                          key: 'chats',
                                          label: getWordWithNumber(chats, ['чат', 'чата', 'чатов']).toLowerCase(),
                                          tooltip: '',
                                      }
                                    : null,
                                keywords > 0
                                    ? {
                                          key: 'keywords',
                                          label: getWordWithNumber(keywords, [
                                              'ключевое слово',
                                              'ключевых слов',
                                              'ключевых слов',
                                          ]),
                                          tooltip: '',
                                      }
                                    : null,
                            ] as { key: string; label: React.ReactNode; tooltip: string }[]
                        )
                            .filter(
                                (chip): chip is { key: string; label: React.ReactNode; tooltip: string } =>
                                    chip !== null
                            )
                            .map((chip) => {
                                // Вычисляем светлый фон на основе цвета тарифа
                                const hexToRgb = (hex: string) => {
                                    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
                                    return result
                                        ? {
                                              r: parseInt(result[1], 16),
                                              g: parseInt(result[2], 16),
                                              b: parseInt(result[3], 16),
                                          }
                                        : null;
                                };
                                const rgb = hexToRgb(tariffColor);
                                const backgroundRgba = rgb ? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.08)` : 'transparent';

                                const commonStyle = {
                                    border: `1.5px solid ${tariffColor}`,
                                    color: 'var(--lm-text)',
                                    background: backgroundRgba,
                                    fontWeight: 600,
                                    borderRadius: '12px',
                                    fontSize: 15,
                                };

                                return (
                                    <Chip
                                        key={chip.key}
                                        label={chip.label}
                                        size="small"
                                        sx={commonStyle}
                                    />
                                );
                            })}
                    </Box>

                    {/* Опции и функции (спам-фильтр, уведомления и т.д.) */}
                    {(spam_filter_enabled ||
                        telegram_notifications_enabled ||
                        leads_export_enabled ||
                        setup_service_enabled ||
                        ai_analysis_enabled) && (
                        <Box
                            display="flex"
                            flexDirection="row"
                            flexWrap="wrap"
                            gap={1}
                        >
                            {(
                                [
                                    spam_filter_enabled
                                        ? {
                                              key: 'spam',
                                              label: (
                                                  <Box
                                                      component="span"
                                                      sx={{ display: 'flex', alignItems: 'center' }}
                                                  >
                                                      <SpamFilterIcon />
                                                      Спам-фильтр
                                                  </Box>
                                              ),
                                              tooltip: 'Автоматическая фильтрация спама и нерелевантных сообщений',
                                          }
                                        : null,
                                    telegram_notifications_enabled
                                        ? {
                                              key: 'telegram',
                                              label: (
                                                  <Box
                                                      component="span"
                                                      sx={{ display: 'flex', alignItems: 'center' }}
                                                  >
                                                      <TelegramNotificationsIcon />
                                                      Telegram уведомления
                                                  </Box>
                                              ),
                                              tooltip: 'Уведомления о новых лидах в Telegram',
                                          }
                                        : null,
                                    leads_export_enabled
                                        ? {
                                              key: 'export',
                                              label: (
                                                  <Box
                                                      component="span"
                                                      sx={{ display: 'flex', alignItems: 'center' }}
                                                  >
                                                      <ExportLeadsIcon />
                                                      Выгрузка лидов
                                                  </Box>
                                              ),
                                              tooltip: 'Экспорт лидов в CSV/Excel',
                                          }
                                        : null,
                                    setup_service_enabled
                                        ? {
                                              key: 'setup',
                                              label: (
                                                  <Box
                                                      component="span"
                                                      sx={{ display: 'flex', alignItems: 'center' }}
                                                  >
                                                      <SetupServiceIcon />
                                                      Настройка под ключ
                                                  </Box>
                                              ),
                                              tooltip: 'Помощь с настройкой сервиса',
                                          }
                                        : null,
                                    ai_analysis_enabled && !isCustomPlan
                                        ? {
                                              key: 'ai',
                                              label: (
                                                  <Box
                                                      component="span"
                                                      sx={{
                                                          fontWeight: 700,
                                                          display: 'flex',
                                                          alignItems: 'center',
                                                          gap: '4px',
                                                      }}
                                                  >
                                                      <AIIcon
                                                          style={{ width: 18, height: 18, color: 'var(--lm-text)' }}
                                                      />
                                                      ИИ анализ лидов
                                                  </Box>
                                              ),
                                              tooltip:
                                                  'Искусственный интеллект определяет релевантность сообщений для вашего бизнеса',
                                          }
                                        : null,
                                ] as { key: string; label: React.ReactNode; tooltip: string }[]
                            )
                                .filter(
                                    (chip): chip is { key: string; label: React.ReactNode; tooltip: string } =>
                                        chip !== null
                                )
                                .map((chip) => {
                                    const commonStyle = {
                                        border: '1.5px solid #000',
                                        color: 'var(--lm-text)',
                                        background: 'none',
                                        fontWeight: 500,
                                        borderRadius: '12px',
                                        fontSize: 15,
                                    };

                                    if (chip.key === 'ai') {
                                        return (
                                            <Tooltip
                                                key={chip.key}
                                                title={chip.tooltip}
                                                arrow
                                            >
                                                <Chip
                                                    label={chip.label}
                                                    size="small"
                                                    sx={{
                                                        border: '1px solid #E0E3E7',
                                                        color: 'var(--lm-text)',
                                                        background: 'var(--lm-surface)',
                                                        fontWeight: 700,
                                                        borderRadius: '16px',
                                                        fontSize: 15,
                                                        cursor: 'pointer',
                                                        boxShadow:
                                                            '-6px -6px 24px 0px rgba(0, 163, 255, 0.30), 6px 6px 24px 0px rgba(250, 0, 255, 0.30)',
                                                    }}
                                                />
                                            </Tooltip>
                                        );
                                    }

                                    if (chip.key === 'setup') {
                                        return (
                                            <Tooltip
                                                key={chip.key}
                                                title={chip.tooltip}
                                                arrow
                                            >
                                                <Chip
                                                    label={chip.label}
                                                    size="small"
                                                    sx={{
                                                        border: '1px solid #000',
                                                        color: 'var(--lm-text)',
                                                        background: 'transparent',
                                                        fontWeight: 600,
                                                        borderRadius: '12px',
                                                        fontSize: 15,
                                                        cursor: 'pointer',
                                                    }}
                                                />
                                            </Tooltip>
                                        );
                                    }

                                    return (
                                        <Tooltip
                                            key={chip.key}
                                            title={chip.tooltip}
                                            arrow
                                        >
                                            <Chip
                                                label={chip.label}
                                                size="small"
                                                sx={{
                                                    border: '1.5px solid #D1D5DB',
                                                    color: 'var(--lm-text)',
                                                    background: 'rgba(209, 213, 219, 0.15)',
                                                    fontWeight: 600,
                                                    borderRadius: '12px',
                                                    fontSize: 15,
                                                    cursor: 'pointer',
                                                }}
                                            />
                                        </Tooltip>
                                    );
                                })}
                        </Box>
                    )}
                </Box>
            )}

            {/* Конфигуратор для индивидуального тарифа */}
            {isCustomPlan && customPlanConfigurator && (
                <Box onClick={(e) => e.stopPropagation()}>{customPlanConfigurator}</Box>
            )}

            {/* Опции для индивидуального тарифа (внизу карточки) */}
            {isCustomPlan && (
                <Box
                    mt={2}
                    pt={2}
                    sx={{
                        borderTop: '1px solid var(--lm-border)',
                    }}
                >
                    <Box
                        display="flex"
                        flexDirection="row"
                        flexWrap="wrap"
                        alignItems="center"
                        gap={1}
                    >
                        <Typography
                            variant="body2"
                            color="text.primary"
                            sx={{
                                fontWeight: 600,
                                fontSize: '14px',
                            }}
                        >
                            Включено в тариф:
                        </Typography>
                        {spam_filter_enabled && (
                            <Chip
                                label={
                                    <Box
                                        component="span"
                                        sx={{ display: 'flex', alignItems: 'center' }}
                                    >
                                        <SpamFilterIcon />
                                        Спам фильтр
                                    </Box>
                                }
                                size="small"
                                sx={{
                                    border: '1.5px solid #D1D5DB',
                                    background: 'rgba(209, 213, 219, 0.15)',
                                    color: 'var(--lm-text)',
                                    fontWeight: 600,
                                    borderRadius: '12px',
                                }}
                            />
                        )}
                        {telegram_notifications_enabled && (
                            <Chip
                                label={
                                    <Box
                                        component="span"
                                        sx={{ display: 'flex', alignItems: 'center' }}
                                    >
                                        <TelegramNotificationsIcon />
                                        Telegram уведомления
                                    </Box>
                                }
                                size="small"
                                sx={{
                                    border: '1.5px solid #D1D5DB',
                                    background: 'rgba(209, 213, 219, 0.15)',
                                    color: 'var(--lm-text)',
                                    fontWeight: 600,
                                    borderRadius: '12px',
                                }}
                            />
                        )}
                        {leads_export_enabled && (
                            <Chip
                                label={
                                    <Box
                                        component="span"
                                        sx={{ display: 'flex', alignItems: 'center' }}
                                    >
                                        <ExportLeadsIcon />
                                        Выгрузка лидов
                                    </Box>
                                }
                                size="small"
                                sx={{
                                    border: '1.5px solid #D1D5DB',
                                    background: 'rgba(209, 213, 219, 0.15)',
                                    color: 'var(--lm-text)',
                                    fontWeight: 600,
                                    borderRadius: '12px',
                                }}
                            />
                        )}
                    </Box>
                </Box>
            )}

            {/* Дополнительная информация для текущего тарифа */}
            {isCurrentTariff && (
                <Box
                    mt={2}
                    pt={2}
                    sx={{
                        borderTop: '1px solid var(--lm-border)',
                    }}
                >
                    <Box mb={2}>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            mb={0.5}
                        >
                            <Typography variant="body2">Оплата в месяц</Typography>
                            <Typography
                                variant="body2"
                                textAlign="right"
                            >
                                {formatNumber(price)} ₽
                            </Typography>
                        </Box>
                        {start_date && (
                            <Box
                                display="flex"
                                justifyContent="space-between"
                                mb={0.5}
                            >
                                <Typography variant="body2">Действует от</Typography>
                                <Typography
                                    variant="body2"
                                    textAlign="right"
                                >
                                    {dayjs(start_date).format('DD.MM.YYYY')}
                                </Typography>
                            </Box>
                        )}
                        {end_date && (
                            <Box
                                display="flex"
                                justifyContent="space-between"
                                mb={0.5}
                            >
                                <Typography variant="body2">Действует до</Typography>
                                <Typography
                                    variant="body2"
                                    textAlign="right"
                                >
                                    {dayjs(end_date).format('DD.MM.YYYY')}
                                </Typography>
                            </Box>
                        )}
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            mb={0.5}
                        >
                            <Typography variant="body2">Следующая оплата</Typography>
                            <Typography
                                variant="body2"
                                textAlign="right"
                            >
                                {is_recurring && end_date ? dayjs(end_date).format('DD.MM.YYYY') : '-'}
                            </Typography>
                        </Box>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                        >
                            <Typography variant="body2">Способ оплаты</Typography>
                            <Typography
                                variant="body2"
                                textAlign="right"
                            >
                                ЮKassa
                            </Typography>
                        </Box>
                    </Box>

                    {/* Кнопка перехода на годовой план */}
                    {duration_months !== undefined &&
                        duration_months < 12 &&
                        onUpgradeToAnnual &&
                        (() => {
                            const periodsInfo = getPeriodsInfo(price);
                            const annualDiscount = periodsInfo[12]?.sale || 0;
                            return (
                                <Box mb={0.5}>
                                    <Button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onUpgradeToAnnual();
                                        }}
                                        variant="contained"
                                        color="primary"
                                        size="medium"
                                        fullWidth
                                        endIcon={
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                gap={0.5}
                                            >
                                                <Chip
                                                    color="default"
                                                    label={`-${annualDiscount}%`}
                                                    size="small"
                                                />
                                                <ArrowRight />
                                            </Box>
                                        }
                                    >
                                        Перейти на годовой план
                                    </Button>
                                </Box>
                            );
                        })()}

                    {/* Кнопка выбора другого тарифа */}
                    {onSelectOtherTariff && (
                        <Box mb={0.5}>
                            <Button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onSelectOtherTariff();
                                }}
                                variant="outlined"
                                color="primary"
                                size="medium"
                                fullWidth
                            >
                                Выбрать другой тариф
                            </Button>
                        </Box>
                    )}

                    {/* Кнопка приостановки подписки (доступна только при включенном автопродлении) */}
                    {onCancelSubscription && is_recurring && (
                        <Box>
                            <TransparentButton
                                disabled={isCanceling || !is_active}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onCancelSubscription();
                                }}
                                variant="contained"
                                color="inherit"
                                size="medium"
                                fullWidth
                            >
                                {is_active ? 'Приостановить продление' : 'Подписка приостановлена'}
                            </TransparentButton>
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );

    // Если тариф недоступен и есть причина, оборачиваем в Tooltip
    if (disabled && disabledReason) {
        return (
            <Tooltip
                title={disabledReason}
                arrow
                placement="top"
            >
                <span>{cardContent}</span>
            </Tooltip>
        );
    }

    return cardContent;
};

export default SubscriptionItem;
